'use client';

import { useCallback, useEffect, useState } from 'react';

import { FormSubmissionModal } from '@/components/forms/FormSubmissionModal/FormSubmissionModal';

import type { FieldConfig } from '@/types/forms';
import type { DefaultValues, FieldValues } from 'react-hook-form';
import type { ZodType } from 'zod';
import type { SecurityConfig, SmartFormModalConfig } from './types';

type SmartFormModalControllerProps<T extends FieldValues> = {
  formId: string;
  schema: ZodType<T>;
  fields: FieldConfig[];
  defaultValues: DefaultValues<T>;
  title?: string;
  description?: string;
  submitLabel?: string;
  columns?: 1 | 2;
  security?: SecurityConfig;
  metadata?: Record<string, unknown>;
  buildMetadata?: () => Record<string, unknown>;
  recipientContext?: Record<string, unknown>;
  triggerHash?: string;
  anchorId?: string;
  modal?: SmartFormModalConfig;
};

function normalizeHash(hash: string): string {
  return hash.startsWith('#') ? hash.slice(1) : hash;
}

function getMatchingHash(formId: string, triggerHash?: string): string {
  if (triggerHash) {
    return normalizeHash(triggerHash);
  }
  return `${formId}-modal`;
}

function getCurrentHash(): string | null {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash;
  return hash ? normalizeHash(hash) : null;
}

function smoothScrollToAnchor(anchorId: string) {
  const el = document.getElementById(anchorId);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

function setHash(hash: string) {
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${window.location.search}${hash}`,
  );
}

function clearHash() {
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${window.location.search}`,
  );
}

export function SmartFormModalController<T extends FieldValues>({
  formId,
  schema,
  fields,
  defaultValues,
  title,
  description,
  submitLabel,
  columns,
  security,
  metadata,
  buildMetadata,
  recipientContext,
  triggerHash,
  anchorId,
  modal,
}: SmartFormModalControllerProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const targetHash = getMatchingHash(formId, triggerHash);

  const tryOpenFromHash = useCallback(() => {
    const hash = getCurrentHash();
    if (hash !== targetHash) return;

    if (anchorId && document.getElementById(anchorId)) {
      smoothScrollToAnchor(anchorId);
      clearHash();
    } else {
      setHasBeenOpened(true);
      setIsOpen(true);
    }
  }, [targetHash, anchorId]);

  useEffect(() => {
    tryOpenFromHash();
  }, [tryOpenFromHash]);

  useEffect(() => {
    const handleHashChange = () => {
      tryOpenFromHash();
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [tryOpenFromHash]);

  useEffect(() => {
    if (!targetHash) return;

    const handleClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const anchor = target.closest<HTMLAnchorElement>('a[href]');
      if (!anchor) return;

      let url: URL;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }

      const hash = url.hash;
      if (!hash) return;

      if (normalizeHash(hash) !== targetHash) return;

      event.preventDefault();

      if (anchorId && document.getElementById(anchorId)) {
        smoothScrollToAnchor(anchorId);
        setHash(hash);
      } else {
        setHasBeenOpened(true);
        setIsOpen(true);
        clearHash();
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [anchorId, targetHash]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      if (getCurrentHash() === targetHash) {
        clearHash();
      }
    }
  };

  return hasBeenOpened ? (
    <FormSubmissionModal<T>
      open={isOpen}
      onOpenChange={handleOpenChange}
      formId={formId}
      schema={schema}
      fields={fields}
      defaultValues={defaultValues}
      title={title ?? ''}
      description={description}
      submitLabel={submitLabel}
      columns={columns}
      metadata={metadata}
      buildMetadata={buildMetadata}
      recipientContext={
        recipientContext as {
          doctorSlug?: string;
          doctorApiId?: string | number | null;
        }
      }
      security={security}
      modal={modal}
    />
  ) : null;
}
